// src/components/OpportunityView.jsx
//
// Phase N — Target counties opportunity ranking.
//
// Different lens from Market Share (Phase M). Market share answers
// "how are we doing in our existing footprint." Opportunity asks
// "where should we look next."
//
// For every county in LFG's geographic thesis (OH + bordering states,
// plus MD/VA for the Harper's Ferry, WV opportunity right on that
// tripoint), score by:
//   - addressable market = annual deaths (Phase M data)
//   - saturation = deaths per funeral home (Phase N data, Census CBP)
//   - proximity to nearest existing LFG location
//   - existing-LFG penalty (don't recommend buying counties we own)
//
// All scoring is client-side so we can tune weights without redeploying
// SQL views. Server provides raw facts via the county_market_facts view
// (already nationwide — county_market_facts and US_COUNTY_CENTROIDS both
// cover all 3,144 US counties; THESIS_STATES is purely a client-side
// filter, so widening it needs no new data, just this list).

import React, { useEffect, useMemo, useState } from 'react';
import { Target, Loader2, MapPin, Building2, Search } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { US_COUNTY_CENTROIDS } from '../data/usCountyCentroids';

// LFG thesis: Ohio + states bordering Ohio, plus MD/VA. The platform
// exists to support acquisitions in this footprint; out-of-thesis
// counties are excluded by default.
const THESIS_STATES = ['OH', 'PA', 'WV', 'KY', 'IN', 'MI', 'MD', 'VA'];

const STATE_FIPS_TO_CODE = {
  '39': 'OH', '42': 'PA', '54': 'WV', '21': 'KY', '18': 'IN', '26': 'MI', '24': 'MD', '51': 'VA',
};

function haversineMiles(lat1, lng1, lat2, lng2) {
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) ** 2 + Math.cos(lat1 * Math.PI/180) * Math.cos(lat2 * Math.PI/180) * Math.sin(dLng/2) ** 2;
  return 3959 * 2 * Math.asin(Math.sqrt(a));
}

function SectionCard({ title, count, icon: Icon, accent = '#1A3665', children, action }) {
  return (
    <section
      className="rounded-2xl mb-6 overflow-hidden"
      style={{
        backgroundColor: '#fff',
        border: '1px solid #E5E9F0',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.03)',
      }}
    >
      <div className="px-6 py-4 flex items-center justify-between gap-3 flex-wrap" style={{ borderBottom: '1px solid #F0F2F6' }}>
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: `linear-gradient(135deg, ${accent} 0%, ${accent}CC 100%)`, boxShadow: `0 2px 6px ${accent}33` }}
          >
            {Icon && <Icon className="w-4 h-4 text-white" strokeWidth={2.2} />}
          </div>
          <div>
            <h2 className="text-base font-semibold tracking-tight" style={{ color: '#0F172A', letterSpacing: '-0.01em' }}>{title}</h2>
            {typeof count === 'number' && (
              <div className="text-xs tabular-nums mt-0.5" style={{ color: '#64748B' }}>
                {count} row{count === 1 ? '' : 's'}
              </div>
            )}
          </div>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function OpportunityView({ organization }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [facts, setFacts] = useState([]);
  const [lfgLocs, setLfgLocs] = useState([]);
  const [stateFilter, setStateFilter] = useState('all');
  const [minDeaths, setMinDeaths] = useState(500);
  // Alfie: search by county name (e.g. "Frederick") to jump straight to a
  // target. City-name lookup (typing "Winchester, VA" directly) resolves
  // via geocode-city-to-county (Google Geocoding API server-side) below,
  // then feeds the result into this same box.
  const [countySearch, setCountySearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [cityLookup, setCityLookup] = useState({ status: 'idle', message: '' }); // idle | loading | ok | error

  const resolveCityToCounty = async () => {
    if (!citySearch.trim()) return;
    setCityLookup({ status: 'loading', message: '' });
    try {
      const spUrl = import.meta.env.VITE_SUPABASE_URL;
      const spKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const res = await fetch(`${spUrl}/functions/v1/geocode-city-to-county`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: spKey, Authorization: `Bearer ${spKey}` },
        body: JSON.stringify({ query: citySearch.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Lookup failed');
      if (!data.found) {
        setCityLookup({ status: 'error', message: `Couldn't resolve "${citySearch}" to a county.` });
        return;
      }
      setCountySearch(data.county_name);
      setStateFilter(THESIS_STATES.includes(data.state) ? data.state : 'all');
      setCityLookup({
        status: 'ok',
        message: THESIS_STATES.includes(data.state)
          ? `${data.county_name}, ${data.state}`
          : `${data.county_name}, ${data.state} — outside the current thesis states, so it won't show in results below.`,
      });
    } catch (e) {
      setCityLookup({ status: 'error', message: e.message });
    }
  };
  const [includeExistingLFG, setIncludeExistingLFG] = useState(false);
  // Phase N.3 — demographics factor is OFF by default. Toggling on adds an
  // age-65+ multiplier to the score (counties with high senior population
  // are leading indicators of future death volume).
  const [applyDemographics, setApplyDemographics] = useState(false);
  const [preset, setPreset] = useState('default');

  // Centroid lookup for haversine math + display
  const centroidByFips = useMemo(() => {
    const m = {};
    for (const c of US_COUNTY_CENTROIDS) m[c.fips] = c;
    return m;
  }, []);

  useEffect(() => {
    if (!organization?.id) return;
    let cancel = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const [factsRes, lfgRes] = await Promise.all([
          supabase.from('county_market_facts')
            .select('county_fips, state, deaths, deaths_year, funeral_homes, fhs_year, deaths_per_fh, median_household_income, income_year, cremation_pct, cremation_year, age_65plus_pct, age_year')
            // Filter to thesis states server-side. Without this, the 3,144
            // US-wide row count bumps into PostgREST's row cap and OH/PA/
            // WV/MI rows (FIPS > 21009) get silently dropped from the
            // first-page response.
            .in('state', THESIS_STATES),
          supabase.from('lfg_locations')
            .select('id, name, county_fips, latitude, longitude, estimated_annual_calls')
            .eq('organization_id', organization.id)
            .eq('is_active', true),
        ]);
        if (factsRes.error) throw factsRes.error;
        if (lfgRes.error) throw lfgRes.error;
        if (cancel) return;
        setFacts(factsRes.data || []);
        // Coerce numeric → Number; PostgREST returns numerics as strings.
        setLfgLocs((lfgRes.data || []).map(l => ({
          ...l,
          _lat: l.latitude  != null ? Number(l.latitude)  : null,
          _lng: l.longitude != null ? Number(l.longitude) : null,
        })).filter(l => Number.isFinite(l._lat) && Number.isFinite(l._lng)));
      } catch (e) {
        if (!cancel) setError(e.message);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [organization?.id]);

  // Set of county_fips where LFG already operates — for the "exclude
  // counties we own" toggle.
  const lfgCountyFips = useMemo(() => {
    return new Set(lfgLocs.map(l => l.county_fips).filter(Boolean));
  }, [lfgLocs]);

  // Score each in-thesis county.
  const scored = useMemo(() => {
    if (!facts.length) return [];
    const rows = [];
    for (const f of facts) {
      const stateCode = f.state || STATE_FIPS_TO_CODE[f.county_fips.slice(0, 2)];
      if (!stateCode) continue;                          // out of thesis
      if (!THESIS_STATES.includes(stateCode)) continue;
      if (stateFilter !== 'all' && stateCode !== stateFilter) continue;
      if (!includeExistingLFG && lfgCountyFips.has(f.county_fips)) continue;
      if ((f.deaths || 0) < minDeaths) continue;

      const cent = centroidByFips[f.county_fips];
      if (!cent) continue;
      if (countySearch.trim() && !cent.name.toLowerCase().includes(countySearch.trim().toLowerCase())) continue;

      // Distance to nearest LFG location
      let nearestMiles = Infinity, nearestName = null;
      for (const l of lfgLocs) {
        const m = haversineMiles(cent.lat, cent.lng, l._lat, l._lng);
        if (m < nearestMiles) { nearestMiles = m; nearestName = l.name; }
      }
      if (!Number.isFinite(nearestMiles)) nearestMiles = 999;

      // ----- Score components -----
      // proximityFactor: 1.0 at 0mi, decays to 0.1 at 200mi, floor 0.05.
      const proximityFactor = Math.max(0.05, 1 - (nearestMiles / 200));

      // saturationFactor: deaths per FH normalized. National avg ~150;
      // > 200 = undersaturated, < 100 = saturated. Clamped [0.3, 1.5].
      const dpfh = f.deaths_per_fh || (f.deaths / Math.max(1, f.funeral_homes || 1));
      const saturationFactor = f.funeral_homes > 0
        ? Math.max(0.3, Math.min(1.5, dpfh / 150))
        : 0.5;                                           // unknown FH → neutral

      // incomeFactor: dampened sqrt scaling. $65K = 1.0, $130K ≈ 1.41,
      // $40K ≈ 0.78. sqrt because income matters but not linearly —
      // a wealthy county is meaningfully better but not 2x for a 2x income.
      const income = Number(f.median_household_income) || 65000;
      const incomeFactor = Math.max(0.5, Math.min(1.6, Math.sqrt(income / 65000)));

      // revenueFactor: traditional services have ~2x the gross margin
      // of cremations, so lower cremation % = higher revenue per call.
      // 1 + (1 - cremation) * 0.5 → at 0% crem = 1.5, at 100% = 1.0.
      const cremPct = Number(f.cremation_pct) || 60;
      const revenueFactor = 1 + ((100 - cremPct) / 100) * 0.5;

      // ageFactor: leading indicator of future death volume. National
      // 65+ % is ~17%; counties at 25%+ skew sharply older. Multiplier
      // is 1.0 at 17%, scaling to ~1.5 at 30%. Only applied if user
      // opts in via the Demographics toggle.
      const age65 = Number(f.age_65plus_pct);
      const ageFactor = applyDemographics && age65
        ? Math.max(0.7, Math.min(1.5, 1 + (age65 - 17) / 26))
        : 1.0;

      const score = (f.deaths || 0)
        * proximityFactor
        * saturationFactor
        * incomeFactor
        * revenueFactor
        * ageFactor;

      // Estimated total county market value in dollars (TAM, not LFG share).
      // Blends a funeral ticket of $6,000 (premium portfolio avg) and a
      // cremation ticket of $1,500 (Sunrise/CST avg) using the state
      // cremation rate. Useful "addressable $" context for prioritization.
      // Reference points: Walker NW Ohio blended $6,327, Sunrise $1,633, CST $1,412.
      const FUNERAL_TICKET = 6000;
      const CREM_TICKET = 1500;
      const blendedTicket = ((100 - cremPct) / 100) * FUNERAL_TICKET + (cremPct / 100) * CREM_TICKET;
      const estMarketUsd = (f.deaths || 0) * blendedTicket;

      rows.push({
        ...f,
        state: stateCode,
        county_name: cent.name,
        nearest_lfg_miles: nearestMiles,
        nearest_lfg_name: nearestName,
        proximity_factor: proximityFactor,
        saturation_factor: saturationFactor,
        income_factor: incomeFactor,
        revenue_factor: revenueFactor,
        age_factor: ageFactor,
        est_market_usd: estMarketUsd,
        blended_ticket: blendedTicket,
        score,
        is_existing_lfg: lfgCountyFips.has(f.county_fips),
      });
    }
    return rows.sort((a, b) => b.score - a.score);
  }, [facts, lfgLocs, lfgCountyFips, centroidByFips, stateFilter, minDeaths, includeExistingLFG, applyDemographics, countySearch]);

  // Presets configure filters + the demographics toggle in one click.
  // Each preset is a self-contained UI state snapshot — re-applying it
  // resets only what it explicitly sets (rest stays as user had it).
  const applyPreset = (id) => {
    setPreset(id);
    if (id === 'default') {
      setStateFilter('all');
      setMinDeaths(500);
      setApplyDemographics(false);
      setIncludeExistingLFG(false);
    } else if (id === 'retiree-corridor') {
      // Wealthy retiree corridor — high 65+ %, demographics on.
      setStateFilter('all');
      setMinDeaths(500);
      setApplyDemographics(true);
      setIncludeExistingLFG(false);
    } else if (id === 'underserved-rural') {
      // Smaller markets, fewer FHs — usually rural.
      setStateFilter('all');
      setMinDeaths(250);
      setApplyDemographics(false);
      setIncludeExistingLFG(false);
    } else if (id === 'urban-consolidation') {
      // Big metros where consolidation plays > de novo entry.
      setStateFilter('all');
      setMinDeaths(2500);
      setApplyDemographics(false);
      setIncludeExistingLFG(false);
    }
  };

  // Discover-competitors: enqueues a single-county sweep job. The existing
  // process-discovery-queue cron picks it up and runs the actual scan.
  const [discoverPending, setDiscoverPending] = useState({});
  const discoverHere = async (row) => {
    setDiscoverPending(p => ({ ...p, [row.county_fips]: 'pending' }));
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const counties = [{
        fips: row.county_fips,
        name: row.county_name,
        state: row.state,
      }];
      const { error } = await supabase.from('discovery_sweep_jobs').insert({
        organization_id: organization.id,
        created_by: user?.id,
        scope_label: `${row.county_name}, ${row.state} (Targets)`,
        max_results: 200,
        counties,
        county_status: ['pending'],
      });
      if (error) throw error;
      setDiscoverPending(p => ({ ...p, [row.county_fips]: 'queued' }));
    } catch (e) {
      setDiscoverPending(p => ({ ...p, [row.county_fips]: `error: ${e.message}` }));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-8 pb-12">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-3" style={{ color: '#0F172A', letterSpacing: '-0.02em' }}>
          <Target className="w-6 h-6" style={{ color: '#10B981' }} />
          Target counties
        </h1>
        <p className="text-sm mt-1" style={{ color: '#64748B' }}>
          Where to look next. Counties in LFG's thesis (OH + border states), scored by addressable market × proximity × saturation. Independent of who already operates there — this is the early-screening lens.
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-xl px-4 py-3 text-[13px]" style={{ backgroundColor: '#FEE2E2', border: '1px solid #FECACA', color: '#B91C1C' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" style={{ color: '#10B981' }} />
          <p className="text-sm" style={{ color: '#64748B' }}>Loading county facts…</p>
        </div>
      ) : (
        <SectionCard
          title="Ranked opportunity"
          count={scored.length}
          icon={Target}
          accent="#10B981"
          action={(
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={preset}
                onChange={e => applyPreset(e.target.value)}
                className="h-7 text-[12px] rounded-md px-2 cursor-pointer focus:outline-none font-medium"
                style={{ border: '1px solid #DDD6FE', backgroundColor: '#F5F3FF', color: '#6D28D9' }}
                title="Preset filter combinations"
              >
                <option value="default">Preset: default</option>
                <option value="retiree-corridor">Preset: wealthy retiree corridor</option>
                <option value="underserved-rural">Preset: underserved rural</option>
                <option value="urban-consolidation">Preset: urban consolidation</option>
              </select>
              <div className="relative">
                <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#94A3B8' }} />
                <input
                  type="text"
                  value={countySearch}
                  onChange={e => setCountySearch(e.target.value)}
                  placeholder="County name…"
                  title="Search by county name — e.g. type 'Frederick' for Frederick County, VA"
                  className="h-7 text-[12px] rounded-md pl-6 pr-2 w-36 focus:outline-none font-medium"
                  style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A' }}
                />
              </div>
              <div className="flex items-center gap-1">
                <div className="relative">
                  <MapPin className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#94A3B8' }} />
                  <input
                    type="text"
                    value={citySearch}
                    onChange={e => { setCitySearch(e.target.value); setCityLookup({ status: 'idle', message: '' }); }}
                    onKeyDown={e => { if (e.key === 'Enter') resolveCityToCounty(); }}
                    placeholder="Or a city, e.g. Winchester, VA"
                    title="Type a city and press Enter — resolves to its county and fills the box above"
                    className="h-7 text-[12px] rounded-md pl-6 pr-2 w-44 focus:outline-none font-medium"
                    style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A' }}
                  />
                </div>
                <button
                  type="button"
                  onClick={resolveCityToCounty}
                  disabled={cityLookup.status === 'loading' || !citySearch.trim()}
                  className="h-7 px-2 text-[11px] font-medium rounded-md disabled:opacity-50"
                  style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A' }}
                >
                  {cityLookup.status === 'loading' ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Find county'}
                </button>
              </div>
              {cityLookup.message && (
                <span
                  className="text-[11px]"
                  style={{ color: cityLookup.status === 'error' ? '#B91C1C' : '#15803D' }}
                >
                  {cityLookup.message}
                </span>
              )}
              <select
                value={stateFilter}
                onChange={e => { setStateFilter(e.target.value); setPreset('default'); }}
                className="h-7 text-[12px] rounded-md px-2 cursor-pointer focus:outline-none font-medium"
                style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A' }}
              >
                <option value="all">All thesis states</option>
                {THESIS_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select
                value={minDeaths}
                onChange={e => { setMinDeaths(Number(e.target.value)); setPreset('default'); }}
                className="h-7 text-[12px] rounded-md px-2 cursor-pointer focus:outline-none font-medium"
                style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A' }}
              >
                <option value={0}>Any size</option>
                <option value={250}>250+ deaths/yr</option>
                <option value={500}>500+ deaths/yr</option>
                <option value={1000}>1000+ deaths/yr</option>
                <option value={2500}>2500+ deaths/yr</option>
              </select>
              <label className="text-[12px] flex items-center gap-1.5 cursor-pointer" style={{ color: '#475569' }}
                     title="Adds an age-65+ multiplier to the score. High-aging counties are leading indicators of future death volume.">
                <input
                  type="checkbox"
                  checked={applyDemographics}
                  onChange={e => { setApplyDemographics(e.target.checked); setPreset('default'); }}
                  className="cursor-pointer"
                />
                Apply demographics
              </label>
              <label className="text-[12px] flex items-center gap-1.5 cursor-pointer" style={{ color: '#475569' }}>
                <input
                  type="checkbox"
                  checked={includeExistingLFG}
                  onChange={e => { setIncludeExistingLFG(e.target.checked); setPreset('default'); }}
                  className="cursor-pointer"
                />
                Include LFG counties
              </label>
            </div>
          )}
        >
          <div className="px-5 pb-5">
            {/* Summary band */}
            <div className="grid grid-cols-4 gap-3 mb-4">
              <div className="rounded-lg p-3" style={{ backgroundColor: '#EFF4FB', border: '1px solid #DBE6F5' }}>
                <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#1A3665' }}>Counties shown</div>
                <div className="text-2xl font-semibold tabular-nums mt-1" style={{ color: '#1A3665' }}>{scored.length.toLocaleString()}</div>
                <div className="text-[11px] mt-0.5" style={{ color: '#64748B' }}>after filters</div>
              </div>
              <div className="rounded-lg p-3" style={{ backgroundColor: '#FEF3C7', border: '1px solid #FDE68A' }}>
                <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#A16207' }}>Total addressable</div>
                <div className="text-2xl font-semibold tabular-nums mt-1" style={{ color: '#A16207' }}>{scored.reduce((s, r) => s + (r.deaths || 0), 0).toLocaleString()}</div>
                <div className="text-[11px] mt-0.5" style={{ color: '#64748B' }}>annual deaths</div>
              </div>
              <div className="rounded-lg p-3" style={{ backgroundColor: '#DCFCE7', border: '1px solid #BBF7D0' }}>
                <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#15803D' }}>FHs in market</div>
                <div className="text-2xl font-semibold tabular-nums mt-1" style={{ color: '#15803D' }}>{scored.reduce((s, r) => s + (r.funeral_homes || 0), 0).toLocaleString()}</div>
                <div className="text-[11px] mt-0.5" style={{ color: '#64748B' }}>per CBP 2023</div>
              </div>
              <div className="rounded-lg p-3" style={{ backgroundColor: '#F5F3FF', border: '1px solid #DDD6FE' }}>
                <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#6D28D9' }}>Within 50mi LFG</div>
                <div className="text-2xl font-semibold tabular-nums mt-1" style={{ color: '#6D28D9' }}>{scored.filter(r => r.nearest_lfg_miles <= 50).length}</div>
                <div className="text-[11px] mt-0.5" style={{ color: '#64748B' }}>tuck-in candidates</div>
              </div>
            </div>

            {scored.length === 0 ? (
              <div className="py-8 text-center text-[13px] rounded-lg" style={{ color: '#94A3B8', backgroundColor: '#FAFBFD', border: '1px dashed #E5E9F0' }}>
                No counties match the current filters. Loosen "min deaths" or pick a different state.
              </div>
            ) : (
              <div className="max-h-[600px] overflow-y-auto rounded-lg" style={{ border: '1px solid #F0F2F6' }}>
                <table className="w-full text-[12px]">
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#FAFBFD', zIndex: 1 }}>
                    <tr>
                      <th className="text-right px-2 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8', width: '40px' }}>#</th>
                      <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}>County</th>
                      <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}>State</th>
                      <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}>Deaths/yr</th>
                      <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }} title="Number of funeral home establishments per Census CBP 2023">FHs</th>
                      <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }} title="Deaths per funeral home — proxy for saturation. National avg ≈150. Higher = more headroom.">Sat.</th>
                      <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }} title="Median household income (Census SAIPE 2023)">Income</th>
                      <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: applyDemographics ? '#6D28D9' : '#94A3B8' }} title="% of population aged 65 and older (Census PEP 2024). National avg ≈17%. Affects score only when 'Apply demographics' is on.">65+%</th>
                      <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }} title="State cremation rate (CANA 2023). Lower = more revenue per call.">Crem%</th>
                      <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}>To LFG</th>
                      <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }} title="Total addressable market in dollars = deaths × blended ticket (cremation-rate-weighted between $6K funeral and $1.5K cremation)">Market $</th>
                      <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }} title="deaths × proximity × saturation × income × revenue (1-cremation%)">Score</th>
                      <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {scored.slice(0, 200).map((r, i) => {
                      const dpfh = r.deaths_per_fh;
                      const dpfhColor = dpfh == null ? '#94A3B8'
                        : dpfh >= 200 ? '#15803D'
                        : dpfh >= 130 ? '#0F172A'
                        : '#A16207';
                      const proxColor = r.nearest_lfg_miles <= 50 ? '#15803D'
                        : r.nearest_lfg_miles <= 150 ? '#0F172A'
                        : '#94A3B8';
                      const income = Number(r.median_household_income);
                      const incomeColor = !income ? '#94A3B8'
                        : income >= 90000 ? '#15803D'
                        : income >= 65000 ? '#0F172A'
                        : '#A16207';
                      const cremState = Number(r.cremation_pct);
                      const cremColor = !cremState ? '#94A3B8'
                        : cremState <= 50 ? '#15803D'
                        : cremState <= 65 ? '#0F172A'
                        : '#A16207';
                      const discoverState = discoverPending[r.county_fips];
                      return (
                        <tr key={r.county_fips} style={{ borderTop: '1px solid #F0F2F6' }}
                            title={`${r.county_name} County, ${r.state}\nDeaths: ${(r.deaths || 0).toLocaleString()} (${r.deaths_year})\nFHs: ${r.funeral_homes} (CBP ${r.fhs_year})\nDeaths/FH: ${dpfh != null ? dpfh : '—'}\nMedian income: $${income ? income.toLocaleString() : '—'}\nAge 65+: ${r.age_65plus_pct ? Number(r.age_65plus_pct).toFixed(1) + '%' : '—'}\nState cremation: ${cremState || '—'}%\nNearest LFG: ${r.nearest_lfg_name || '—'} (${r.nearest_lfg_miles.toFixed(0)}mi)\nEst. market: $${(r.est_market_usd / 1_000_000).toFixed(1)}M (blended ticket $${Math.round(r.blended_ticket).toLocaleString()})\n\nFactors:\n  proximity ${r.proximity_factor.toFixed(2)}\n  saturation ${r.saturation_factor.toFixed(2)}\n  income ${r.income_factor.toFixed(2)}\n  revenue ${r.revenue_factor.toFixed(2)}${applyDemographics ? `\n  age ${r.age_factor.toFixed(2)}` : ''}${r.is_existing_lfg ? '\n\nLFG already operates here.' : ''}`}>
                          <td className="px-2 py-1.5 text-right tabular-nums text-[11px]" style={{ color: '#94A3B8' }}>{i + 1}</td>
                          <td className="px-3 py-1.5">
                            <div className="flex items-center gap-1.5">
                              {r.is_existing_lfg && (
                                <span className="inline-block text-[9px] font-bold uppercase px-1 rounded flex-shrink-0" style={{ backgroundColor: '#EFF4FB', color: '#1A3665' }}>LFG</span>
                              )}
                              <span style={{ color: '#0F172A' }}>{r.county_name}</span>
                            </div>
                          </td>
                          <td className="px-3 py-1.5" style={{ color: '#475569' }}>{r.state}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums font-semibold" style={{ color: '#0F172A' }}>{(r.deaths || 0).toLocaleString()}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums" style={{ color: '#475569' }}>{r.funeral_homes || '—'}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums" style={{ color: dpfhColor }}>{dpfh != null ? dpfh : '—'}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums" style={{ color: incomeColor }}>{income ? `$${(income / 1000).toFixed(0)}k` : '—'}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums" style={{
                            color: !r.age_65plus_pct ? '#94A3B8'
                              : Number(r.age_65plus_pct) >= 22 ? (applyDemographics ? '#6D28D9' : '#0F172A')
                              : Number(r.age_65plus_pct) >= 17 ? '#475569'
                              : '#94A3B8',
                            fontWeight: applyDemographics && Number(r.age_65plus_pct) >= 22 ? 600 : 400,
                          }}>{r.age_65plus_pct ? `${Number(r.age_65plus_pct).toFixed(1)}%` : '—'}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums" style={{ color: cremColor }}>{cremState ? `${cremState}%` : '—'}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums" style={{ color: proxColor }}>{r.nearest_lfg_miles < 999 ? `${r.nearest_lfg_miles.toFixed(0)}mi` : '—'}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums" style={{ color: r.est_market_usd >= 20_000_000 ? '#15803D' : '#475569' }}>
                            {r.est_market_usd >= 1_000_000 ? `$${(r.est_market_usd / 1_000_000).toFixed(1)}M` : '—'}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums font-semibold" style={{ color: '#0F172A' }}>{Math.round(r.score).toLocaleString()}</td>
                          <td className="px-3 py-1.5 text-right">
                            {discoverState === 'queued' ? (
                              <span className="text-[10px] font-semibold" style={{ color: '#15803D' }}>queued ✓</span>
                            ) : discoverState === 'pending' ? (
                              <Loader2 className="w-3 h-3 animate-spin inline" style={{ color: '#64748B' }} />
                            ) : discoverState && discoverState.startsWith('error') ? (
                              <span className="text-[10px]" style={{ color: '#B91C1C' }} title={discoverState}>error</span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => discoverHere(r)}
                                className="text-[10px] font-semibold px-2 py-0.5 rounded hover:opacity-80 cursor-pointer"
                                style={{ backgroundColor: '#F0FDF4', color: '#166534', border: '1px solid #BBF7D0' }}
                                title={`Enqueue an AI competitor scan for ${r.county_name} County, ${r.state}`}
                              >
                                Discover
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {scored.length > 200 && (
                  <div className="text-[11px] text-center py-2" style={{ color: '#94A3B8', borderTop: '1px solid #F0F2F6' }}>
                    Showing top 200 of {scored.length}. Tighten filters to narrow.
                  </div>
                )}
              </div>
            )}

            <div className="mt-3 text-[11px] leading-relaxed" style={{ color: '#94A3B8' }}>
              <strong style={{ color: '#475569' }}>Score</strong> = deaths × proximity × saturation × income × revenue{applyDemographics ? ' × age' : ''}. All factors are bounded so no single dimension can dominate.
              <br />
              <strong style={{ color: '#475569' }}>Proximity</strong>: 1.0 at 0mi from nearest LFG, decays to 0.05 at 200mi+.
              <strong style={{ color: '#475569' }}> Saturation</strong>: deaths/FH / 150 national avg, clamped [0.3, 1.5]. High = headroom.
              <strong style={{ color: '#475569' }}> Income</strong>: √(median income / $65K), clamped [0.5, 1.6]. Wealthier markets pay more per service.
              <strong style={{ color: '#475569' }}> Revenue</strong>: 1 + (1 − cremation%) × 0.5. Lower cremation = higher per-call margin.
              {applyDemographics && (
                <> <strong style={{ color: '#6D28D9' }}>Age</strong>: 1 + (% 65+ − 17) / 26, clamped [0.7, 1.5]. Counties skewing older are leading indicators of future death volume.</>
              )}
              <br />
              Sources: Census PEP (deaths + age), Census CBP 2023 NAICS 812210 (FHs), Census SAIPE (income), CANA 2023 (state cremation rates). Age factor is opt-in via the Demographics toggle so it doesn't change the default behavior. LFG counties hidden by default.
              <br />
              <strong style={{ color: '#475569' }}>Market $</strong> = total addressable market in dollars, computed as <code>deaths × (cremation% × $1,500 + (1 − cremation%) × $6,000)</code>. Benchmarks: Walker NW Ohio funeral avg $6,327, Sunrise/CST cremation avg $1.4–1.6K (per VYTL Toledo workbook).
              <br />
              <strong style={{ color: '#475569' }}>Discover</strong> enqueues a server-side AI competitor scan for that county. Status visible in Market Share's Discover panel.
            </div>
          </div>
        </SectionCard>
      )}
    </div>
  );
}
